"use client";

import { usePathname } from "next/navigation";
import Logo from "@/assets/icons/logo.svg";
import { Img } from "@/components/image";
import { AppBannerLinks } from "@/features/static-builder/components/mobile-app-banner/app-banner-links";
import type { MobileAppBannerContentType } from "@/features/static-builder/types/mobile-app-banner.type";
import { interpretColors } from "@/lib/helpers/typography";
import { cn } from "@/lib/utils";

type Props = {
  data: MobileAppBannerContentType;
  strapiUrl: string;
  variant?: "default" | "listing" | "checkout" | "panel";
  prioritized?: boolean;
};

type BannerMedia = {
  url: string;
  name: string;
  width: number;
  height: number;
};

const getBannerMedia = (value: unknown): BannerMedia | null => {
  if (!value || typeof value !== "object") return null;

  const mediaWithOptionalData = value as {
    data?: unknown;
    attributes?: unknown;
    url?: unknown;
    name?: unknown;
    width?: unknown;
    height?: unknown;
  };

  const candidate =
    mediaWithOptionalData.data && typeof mediaWithOptionalData.data === "object"
      ? (mediaWithOptionalData.data as Record<string, unknown>)
      : mediaWithOptionalData;
  const source =
    candidate.attributes && typeof candidate.attributes === "object"
      ? (candidate.attributes as Record<string, unknown>)
      : candidate;

  const url = source.url;
  if (typeof url !== "string" || url.length === 0) {
    return null;
  }

  return {
    url,
    name: typeof source.name === "string" ? source.name : "",
    width: typeof source.width === "number" ? source.width : 0,
    height: typeof source.height === "number" ? source.height : 0,
  };
};

const getLogoStyle = (variant: string) => {
  switch (variant) {
    case "listing":
      return "h-[42px] w-[190px] max-md:hidden";
    case "checkout":
      return "h-[42px] w-[190px] max-md:hidden";
    case "panel":
      return "hidden";
    default:
      return "h-[34px] w-[154px] max-md:mb-3.5 md:h-[40px] md:w-[170px] xl:h-[50px] xl:w-[230px]";
  }
};

export const MobileAppBanner = ({
  data,
  strapiUrl,
  variant = "default",
  prioritized = false,
}: Props) => {
  const pathname = usePathname();

  const getImageUrl = (path: string) => {
    if (path.startsWith("http")) {
      return path;
    }
    return `${strapiUrl}${path}`;
  };

  const desktopImage = getBannerMedia(data.mainImageDesktop);
  const mobileImage = getBannerMedia(data.mainImageMobile);
  const iosImage = getBannerMedia(data.iosImage);
  const androidImage = getBannerMedia(data.androidImage);
  const qrCodeImage = getBannerMedia(data.qrCode);

  const desktopImageUrl = desktopImage?.url
    ? getImageUrl(desktopImage.url)
    : undefined;
  const mobileImageUrl = mobileImage?.url
    ? getImageUrl(mobileImage.url)
    : undefined;
  const iosImageUrl = iosImage?.url ? getImageUrl(iosImage.url) : undefined;
  const androidImageUrl = androidImage?.url
    ? getImageUrl(androidImage.url)
    : undefined;
  const qrCodeUrl = qrCodeImage?.url ? getImageUrl(qrCodeImage.url) : undefined;

  if (pathname === "/przesiadka") {
    return null;
  }

  return (
    <div
      className={cn(
        "relative w-full bg-brand-green1-300",
        (variant === "listing" || variant === "checkout") &&
          "overflow-hidden rounded-xl max-md:min-h-[220px]",
        variant === "default" &&
          "min-h-[340px] md:mt-12 md:min-h-[240px] xl:min-h-[270px]",
        variant === "panel" &&
          "min-h-[220px] rounded-xl bg-brand-green2 md:min-h-[170px]",
      )}
    >
      <div
        className={cn(
          "container py-8 max-md:pt-7 xl:py-14",
          (variant === "listing" || variant === "checkout") &&
            "pl-5 max-md:pt-5 md:pl-8",
          variant === "panel" && "pl-8 max-md:pt-2 md:pb-0 xl:py-8",
        )}
      >
        <div
          className={cn(
            "flex flex-col items-start justify-start xl:flex-row xl:items-center",
            variant === "default" && "md:gap-4 xl:gap-13",
            variant === "listing" && "md:gap-4",
            (variant === "checkout" || variant === "panel") &&
              "gap-4 lg:gap-7.5",
          )}
        >
          <div
            className={cn(
              "md:space-y-1 xl:w-[26.8%] xl:space-y-3",
              variant === "listing" && "xl:w-[23%]",
              (variant === "checkout" || variant === "panel") &&
                "md:w-[70%] xl:w-[22%]",
            )}
          >
            <Logo className={getLogoStyle(variant)} />
            <p
              className={cn(
                "hidden font-medium text-brand-white text-xl md:block xl:text-3.5xl xl:leading-10",
                (variant === "listing" ||
                  variant === "checkout" ||
                  variant === "panel") &&
                  "text-lg leading-6 lg:w-[60%] xl:w-full xl:text-2xl xl:leading-8",
              )}
              dangerouslySetInnerHTML={{
                __html: interpretColors(data.description ?? ""),
              }}
            />
          </div>
          <div
            className={cn(
              "hidden items-center md:flex",
              variant === "default" && "gap-7.5 xl:gap-13",
              (variant === "listing" ||
                variant === "checkout" ||
                variant === "panel") &&
                "gap-4 xl:gap-7.5",
            )}
          >
            {qrCodeUrl && qrCodeImage && (
              <Img
                alt={qrCodeImage.name}
                className={cn(
                  "aspect-square object-cover",
                  variant === "default" && "size-[84px] xl:size-[164px]",
                  (variant === "listing" ||
                    variant === "checkout" ||
                    variant === "panel") &&
                    "size-[108px]",
                )}
                height={qrCodeImage.height}
                src={qrCodeUrl}
                width={qrCodeImage.width}
              />
            )}
            <div className="flex flex-col gap-2.5">
              {androidImageUrl && androidImage && (
                <a
                  href={data.androidLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Img
                    alt={androidImage.name}
                    className="w-[112px] xl:w-[145px]"
                    height={androidImage.height}
                    src={androidImageUrl}
                    width={androidImage.width}
                  />
                </a>
              )}
              {iosImageUrl && iosImage && (
                <a
                  href={data.iosLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Img
                    alt={iosImage.name}
                    className="w-[112px] xl:w-[145px]"
                    height={iosImage.height}
                    src={iosImageUrl}
                    width={iosImage.width}
                  />
                </a>
              )}
            </div>
          </div>
          <AppBannerLinks
            androidImageUrl={androidImageUrl}
            appBanner={data}
            iosImageUrl={iosImageUrl}
            variant={variant}
          />
          <p
            className={cn(
              "mt-6 w-[55%] font-semibold text-brand-white text-xl leading-6 md:hidden",
              variant === "listing" && "mt-2 text-lg leading-6.5",
              variant === "checkout" &&
                "mt-0 w-[53%] xs:w-[40%] text-lg leading-6.5 sm:w-[50%]",
              variant === "panel" &&
                "mt-0 w-[67%] xs:w-[40%] text-lg leading-6.5 sm:w-[50%]",
            )}
            dangerouslySetInnerHTML={{
              __html: interpretColors(data.description ?? ""),
            }}
          />
        </div>
      </div>
      {/* Desktop */}
      {desktopImageUrl && desktopImage && (
        <Img
          alt={desktopImage.name}
          className={cn(
            "absolute right-0 bottom-0 hidden md:block",
            variant === "default" && "w-[432px] xl:w-[472px]",
            variant === "listing" && "md:w-[360px] lg:w-[390px] xl:w-[343px]",
            variant === "checkout" && "md:w-[210px] lg:w-[420px] xl:w-[370px]",
            variant === "panel" && "md:w-[220px] lg:w-[450px] xl:w-[340px]",
          )}
          fetchPriority={prioritized ? "high" : "auto"}
          height={desktopImage.height}
          loading={prioritized ? "eager" : "lazy"}
          src={desktopImageUrl}
          width={desktopImage.width}
        />
      )}
      {/* Mobile */}
      {mobileImageUrl && mobileImage && (
        <Img
          alt={mobileImage.name}
          className={cn(
            "absolute right-0 bottom-0 w-full xs:object-cover object-fill md:hidden",
            variant === "default" && "h-full xs:w-[400px]",
            variant === "listing" && "w-[350px]",
            variant === "checkout" && "w-[180px] xs:w-[190px]",
            variant === "panel" && "w-[450px] xs:w-[360px]",
          )}
          fetchPriority={prioritized ? "high" : "auto"}
          height={mobileImage.height}
          loading={prioritized ? "eager" : "lazy"}
          src={mobileImageUrl}
          width={mobileImage.width}
        />
      )}
    </div>
  );
};
